import React, { useState, useEffect, useCallback } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';

import {
  getApproval,
  getGroupDetails,
  getNotification,
  getUserRoleDetails,
} from '../services/api/profile.service';
import { selectUserProfile, selectUserProfileImage } from '../store/selectors/profile.selector';
import { selectMytripsCount } from '../store/selectors/mytrips.selector';
import { selectTokenData } from '../store/selectors/auth.selector';

import NavigateRow from '../components/NavigateRow';
import ProfileHeader from '../components/ProfileHeader';
import ShadowBox from '../components/ShadowBox';

import ProfileViewer from '../components/me/ProfileViewer';
import PassportViewer from '../components/me/PassportViewer';
import LoyaltyViewer from '../components/me/LoyaltyViewer';
import CreditCardViewer from '../components/me/CreditCardViewer';
import ApprovalViewer from '../components/me/ApprovalViewer';

import { getCountries } from '../utils/country.utils';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import { Modals } from '../constants/ScreenNames';
import { Color, CommonProperties } from '../styles';
import Messages from '../components/event/Messages';
import { pageKeys } from '../constants/PageKeys';
import { CREW_ENABLED } from '../config/env';
import { isNullOrEmpty } from '../utils/common.utils';

const MeScreen = ({ navigation }) => {
  const { tKey } = useComponentTKey('MeScreen');
  const { t } = useTranslation(i18nNS.me);
  const [userViewerVisible, setUserViewerVisible] = useState(false);
  const [passportViewerVisible, setpassportViewerVisible] = useState(false);
  const [creditViewerVisible, setCreditViewerVisible] = useState(false);
  const [loyaltyViewerVisible, setLoyaltyViewerVisible] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [approvalList, setApprovalList] = useState([]);
  const [approvalViewerVisible, setApprovalViewerVisible] = useState(false);
  const [approvalListViewerVisible, setApprovalListViewerVisible] = useState(false);

  const [groupDetails, setGroupDetails] = useState({});
  const [userRoleDetails, setUserRoleDetails] = useState({});
  const [notificationList, setNotificationList] = useState({});

  const myTrips = useSelector(selectMytripsCount);
  const userProfile = useSelector(selectUserProfile);

  const userImage = useSelector(selectUserProfileImage);
  const tokenData = useSelector(selectTokenData);

  const BuildUpdateAllowed = () => {
    // add logic to disable the updates if needed
    return !CREW_ENABLED;
  };

  const isUpdateAllowed = BuildUpdateAllowed();

  useEffect(() => {
    if (userRoleDetails || groupDetails) {
      showApprovalTab();
    }
  }, [userRoleDetails, groupDetails]);

  useEffect(() => {
    (async () => {
      const res = await getGroupDetails();
      if (res) {
        setGroupDetails(res);
      }
    })();

    (async () => {
      const res = await getUserRoleDetails();
      setUserRoleDetails(res);
    })();

    (async () => {
      const res = await getNotification();

      if (res) {
        setNotificationList(res.NotificationRecipientList);
      }
    })();

    (async () => {
      const res = await getCountries();

      setCountryList(res);
    })();

    (async () => {
      const res = await getApproval();
      if (Array.isArray(res) && res.length > 0) {
        setApprovalList(
          res.map((i) => ({
            label: i.FirstName,
            value: i.Id,
          }))
        );
      }
    })();
  }, []);

  const showApprovalTab = () => {
    const ROLE_USER_1 = 1;
    const ROLE_USER_2 = 2;
    const ROLE_USER_4 = 4;
    const ROLE_USER_8 = 8;
    const ROLE_USER_16 = 16;
    const ROLE_USER_31 = 31;
    const ROLE_USER_131072 = 131072;

    let isSuperAdmin = false;
    let isCompanyAdmin = false;
    let isAdminArranger = false;

    if (
      !isNullOrEmpty(userRoleDetails.IsMyTraveller) &&
      !(userRoleDetails.IsMyTraveller === 'true')
    ) {
      if (!isNullOrEmpty(userRoleDetails.RoleType)) {
        if (userRoleDetails.RoleType.ToLower().Contains('super')) {
          isSuperAdmin = true;
        } else if (userRoleDetails.RoleType.ToLower().Contains('administrator')) {
          isCompanyAdmin = true;
        }
      }
    } else {
      if (userRoleDetails != null && userRoleDetails.UserRole > 0) {
        if (
          (userRoleDetails.UserRole & ROLE_USER_1) > 0 &&
          (userRoleDetails.UserRole & ROLE_USER_2) > 0 &&
          (userRoleDetails.UserRole & ROLE_USER_4) > 0 &&
          (userRoleDetails.UserRole & ROLE_USER_8) > 0 &&
          (userRoleDetails.UserRole & ROLE_USER_16) > 0 &&
          (userRoleDetails.UserRole & ROLE_USER_31) > 0
        ) {
          isSuperAdmin = true;
        }

        if ((userRoleDetails.UserRole & ROLE_USER_4) > 0) {
          isCompanyAdmin = true;
        }
        if ((userRoleDetails.UserRole & ROLE_USER_131072) > 0) {
          isAdminArranger = true;
        }
      }
    }

    if (
      (userRoleDetails.IsMyTraveller !== 'true' &&
        !isNullOrEmpty(groupDetails) &&
        groupDetails?.IsApproverSelectAllowed) ||
      isSuperAdmin ||
      isCompanyAdmin ||
      isAdminArranger
    ) {
      setApprovalViewerVisible(true);
    }
  };

  const onProfilePress = () => {
    setUserViewerVisible(!userViewerVisible);
  };
  const onPassportPress = useCallback(() => {
    setpassportViewerVisible((visible) => !visible);
  }, []);
  const onCreditCardPress = useCallback(() => {
    setCreditViewerVisible((visible) => !visible);
  }, []);
  const onLoyaltyPress = () => {
    setLoyaltyViewerVisible(!loyaltyViewerVisible);
  };
  const onPasswordPress = () => {
    navigation.navigate(Modals.PasswordModal);
  };
  const onApprovalPress = () => {
    userProfile.IsApproverLocked ? false : setApprovalListViewerVisible((visible) => !visible);
  };
  const onImageProfilePress = () => {};
  const onEditPress = (isUpdateAllowed) => {
    navigation.navigate(Modals.ProfileModal, {
      countryList,
      approvalList,
      groupDetails,
      notificationList,
      userRoleDetails,
      isUpdateAllowed,
    });
  };
  const onEditPassportPress = () => {
    navigation.navigate(Modals.PassportModal, {
      userProfile,
      countryList,
    });
  };

  return (
    <View style={styles.wrap}>
      <ScrollView>
        <ProfileHeader trips={myTrips} imageURL={userImage} />

        {CREW_ENABLED && (
          <View style={{ ...CommonProperties.px3 }}>
            <Messages
              companyKey={tokenData?.companyKey}
              groupKey={tokenData?.groupKey}
              pageKey={pageKeys.HOME}
            />
          </View>
        )}

        <ShadowBox>
          <NavigateRow
            iconName="person"
            activeColor
            text={t(tKey('Profile'))}
            onPressCallback={onProfilePress}
          />
          {userViewerVisible && (
            <ProfileViewer
              profile={userProfile}
              onAvatarPress={onImageProfilePress}
              onEditPress={() => onEditPress(isUpdateAllowed)}
              isUpdateAllowed={isUpdateAllowed}
            />
          )}
          <NavigateRow
            iconName="passport"
            activeColor
            text={t(tKey('PassportInfo'))}
            onPressCallback={onPassportPress}
          />
          {passportViewerVisible && (
            <PassportViewer
              profile={userProfile}
              onEditPress={onEditPassportPress}
              isUpdateAllowed={isUpdateAllowed}
            />
          )}
          <NavigateRow
            iconName="payment"
            activeColor
            text={t(tKey('CreditCard'))}
            onPressCallback={onCreditCardPress}
          />
          {creditViewerVisible && <CreditCardViewer isUpdateAllowed={isUpdateAllowed} />}
          <NavigateRow
            activeColor
            iconName="star"
            text={t(tKey('Loyalty'))}
            onPressCallback={onLoyaltyPress}
          />
          {loyaltyViewerVisible && <LoyaltyViewer isUpdateAllowed={isUpdateAllowed} />}
          <NavigateRow
            iconName="lock"
            activeColor
            text={t(tKey('ChangePassword'))}
            disableAnimation
            onPressCallback={onPasswordPress}
          />
          {approvalViewerVisible && !CREW_ENABLED && (
            <NavigateRow
              iconName="circleCheck"
              disableAnimation={userProfile.IsApproverLocked ? true : false}
              activeColor={userProfile.IsApproverLocked ? false : true}
              text={t(tKey('Approval'))}
              onPressCallback={onApprovalPress}
            />
          )}
          {approvalListViewerVisible && !CREW_ENABLED && (
            <ApprovalViewer
              approvalList={approvalList}
              approvalKey={userProfile?.ApproverKey}
              UserProfileId={userProfile?.UserProfileId}
            />
          )}
        </ShadowBox>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    backgroundColor: Color.accent,
  },
});

export default MeScreen;
